import React, { useState, useRef } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import api, { apiErr, receiptUrl, receiptPreviewUrl } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { Card, Btn, PageHeader, EmptyState, InfoTip } from "@/components/common";
import { fmtDate, taxYears, CURRENCY_FIELD_HELP } from "@/lib/format";
import { irsCategoriesFor } from "@/lib/categoryGate";
import { toast } from "sonner";
import { FileCsv, UploadSimple, Trash, Plus, ArrowRight, ArrowLeft, CheckCircle, Warning, CaretDown, Camera, Receipt, Sparkle, Check, X, FilePdf, Image as ImageIcon, EnvelopeSimple } from "@phosphor-icons/react";
import { CurrencySelect } from "@/components/CurrencySelect";
import { useParties } from "@/lib/parties";
import { usePaymentSources, psLabel, psMeta } from "@/lib/payments";
import { ReceiptViewer } from "@/components/ReceiptViewer";
import { ReceiptQueue } from "./ImportReceiptReview";
import { Stepper, ReviewRow } from "./ImportReviewRow";
import { DecisionDialog } from "@/components/DecisionDialog";
import { DateConfirmBanner, dateFlagMessage, dateConfirmRefusal } from "@/components/DateConfirmBanner";
import { BulkReceiptMatcher } from "./BulkReceiptMatcher";
import { RECEIPT_ACCEPT, uploadFailureState, queueItemPresentation, readingFoundNothing, READING_FOUND_NOTHING, readAmount } from "@/lib/uploads";
import { curCode, readOutsideCurrency, importButtonLabel, stillWaitingForCurrencyMessage } from "@/lib/currencies";

// ── WHAT THE PERSON IS TOLD WHEN A ROW TRIED TO SET THE TAX CREDIT. (ZEN-475)
//
// APPROVED VERBATIM BY MARYSE, 2026-09-04 — ship exactly this, do not reword.
// The same sentence is recorded in backend/routes/misc.py, also marked
// do-not-paraphrase, so the two halves cannot drift into saying different
// things about one rule.
//
// IT NAMES THE EXPENSE, NOT THE BENEFICIARY CARD, and that was her correction:
// the card holds the per-student, per-tax-year AOTC reserve TARGET, while each
// 529-qualified expense carries the actual either/or — be reimbursed by the 529,
// or go toward the AOTC. `aotc_reserved` is that second choice. The ticket's own
// wording sent people to the card, which is the wrong screen.
export const AOTC_IGNORED_NOTICE =
  "Imports never reserve an expense for AOTC — open the expense afterwards to choose between drawing from the 529 and reserving it.";

// Exported and pure so the rule can be tested without driving the whole import
// wizard, matching how this app tests its other notices — see
// pages/projectionExclusionNotice.test.jsx.
//
// STRICTLY GREATER THAN ZERO, and read as a number. A count of nothing must stay
// silent: a notice that appears on every ordinary import is an alarm about
// nothing, on a screen about somebody's taxes. The server only counts a row that
// MEANT to reserve — a row saying "false" reserved nothing and is not counted
// (backend, adversarial review round 4).
export const aotcIgnoredNotice = (data) =>
  Number(data?.aotc_reservations_ignored) > 0 ? AOTC_IGNORED_NOTICE : null;

const uid = () => Math.random().toString(36).slice(2);
const toNum = (v) => { const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, "")); return isNaN(n) ? "" : n; };
const truthy = (v) => ["yes", "true", "1", "y", "eligible", "qualified"].includes(String(v ?? "").trim().toLowerCase());

/**
 * The rows still on screen after an import: everything EXCEPT the rows that
 * were sent. Read from the rows as they are NOW, not as they were when Import
 * was pressed, so a currency picked or a row removed while the request was on
 * its way is kept. (ZEN-829)
 */
export const rowsLeftAfterImport = (rowsNow, sentIds) => rowsNow.filter((d) => !sentIds.has(d._id));

export default function ImportExpenses() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { selectedStudentId } = useAuth();
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const currencies = settings?.currencies || ["USD", "CAD", "EUR", "GBP"];

  const [step, setStep] = useState(1);      // 1 source, 2 mapping(file only), 3 review
  const [source, setSource] = useState("file");
  const [student, setStudent] = useState(selectedStudentId || "");
  // Reset student when beneficiary switcher changes
  React.useEffect(() => { setStudent(selectedStudentId || ""); }, [selectedStudentId]);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null); // {columns, rows, suggested_mapping}
  const [mapping, setMapping] = useState({});
  const [drafts, setDrafts] = useState([]);
  const [payorType, setPayorType] = useState("");      // who paid — no default, must be chosen
  const [payorPartyId, setPayorPartyId] = useState("");
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const { data: parties } = useParties();
  const ownerNick = parties?.owner?.nickname || "Owner";
  // Batch receipt queue
  const [queue, setQueue] = useState([]);
  const [processingCount, setProcessingCount] = useState(0);
  // Side-by-side preview receipt ID
  const [previewReceiptId, setPreviewReceiptId] = useState(null);

  // A queued item belongs to the beneficiary it was uploaded under. When the
  // selection changes, items queued under somebody else leave the queue —
  // otherwise they would sit under the new name and be reviewed there.
  // (ZEN-142 family, arena round 8)
  React.useEffect(() => {
    setQueue((q) => q.filter((item) => !item.studentId || item.studentId === student));
  }, [student]);

  // Top selector is the source of truth — when a specific student is selected, lock to it
  const isStudentLocked = !!selectedStudentId;
  React.useEffect(() => {
    if (selectedStudentId) setStudent(selectedStudentId);
  }, [selectedStudentId]);
  // No silent default — on "All beneficiaries" view, the student select starts empty
  // and uploading is blocked until one is explicitly chosen.

  const studentChosen = !!student;

  const selectedStudentObj = students.find((s) => s.id === student);
  const activeSchools = (selectedStudentObj?.schools || []).filter(s => !s.archived);
  const studentGroup = activeSchools[0]?.expense_group || "post_secondary";
  // THE ELIGIBLE LIST, THROUGH THE GATE. (ZEN-100)
  //
  // This read had no filter at all — unlike the expense dialog, which at least
  // stripped the catch-all per call. So a spreadsheet whose Category column
  // said "Other — enter manually", or anything at all when the beneficiary's
  // education group could not be resolved and the retired flat list took over,
  // was accepted below as a suggested category on the 529-ELIGIBLE side.
  const cats = irsCategoriesFor(studentGroup);

  const reset = () => { setStep(1); setParsed(null); setMapping({}); setDrafts([]); setQueue([]); setPreviewReceiptId(null); if (fileRef.current) fileRef.current.value = ""; };

  const downloadTemplate = () => {
    const headers = "Date,Payee,Description,Amount,Currency,Category,529 Eligible";
    const examples = [
      "2025-09-01,State University,Fall tuition,5000,USD,Tuition & Fees,Yes",
      "2025-09-03,Campus Bookstore,Textbooks,320,USD,Books,Yes",
      "2025-09-05,FreshMart Grocery,Weekly groceries,85.40,USD,Food,Yes",
    ];
    const csv = [headers, ...examples].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = "529-expense-import-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const [dragging, setDragging] = useState(false);
  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const files = e.dataTransfer?.files;
    if (files?.length) {
      for (let i = 0; i < files.length; i++) onFile(files[i]);
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    if (!student) { toast.error("Select a beneficiary first"); return; }
    const name = (file.name || "").toLowerCase();
    const isSpreadsheet = /\.(csv|xlsx|xls)$/.test(name) || /spreadsheet|excel|csv/.test(file.type || "");

    if (isSpreadsheet) {
      // Spreadsheet: old flow (single file → mapping → review)
      setSource("file");
      setBusy(true);
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/import/parse-file", fd);
        setParsed(data); setMapping(data.suggested_mapping || {}); setStep(2);
      } catch (e) { toast.error(apiErr(e)); }
      finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    } else {
      // Receipt image/PDF: batch queue mode
      setSource("image");

      // Layer 3: client-side hash for batch queue dedup
      let fileHash = null;
      try {
        const buf = await file.arrayBuffer();
        const hashBuf = await crypto.subtle.digest("SHA-256", buf);
        fileHash = Array.from(new Uint8Array(hashBuf)).map((b) => b.toString(16).padStart(2, "0")).join("");
        const existing = queue.find((q) => q.fileHash === fileHash);
        if (existing) {
          toast.error(`Duplicate file: "${file.name}" matches already-queued "${existing.filename}"`);
          if (fileRef.current) fileRef.current.value = "";
          return;
        }
      } catch { /* hash unavailable, skip client dedup */ }

      const qId = uid();
      setQueue((q) => [...q, { id: qId, receiptId: null, filename: file.name, contentType: file.type, fileHash, pageCount: 1, draft: null, status: "processing", studentId: student }]);
      setProcessingCount((c) => c + 1);
      try {
        // Upload receipt (stores file + runs AI) — Layer 1 dedup happens server-side (409)
        const uploadFd = new FormData();
        uploadFd.append("file", file); uploadFd.append("student_id", student); uploadFd.append("run_ai", "true");
        const { data: recData } = await api.post("/receipts/upload", uploadFd);
        const receiptId = recData.receipt_id || recData.id;
        const draft = recData.draft || {};
        const pageCount = recData.page_count || 1;
        setQueue((q) => q.map((item) => item.id === qId ? { ...item, receiptId, draft, pageCount, status: recData.ai_error ? "error" : "ready" } : item));
        // THE LABELLED PANEL BELOW IS WHERE THIS RECEIPT GETS REVIEWED, and it
        // reads the stored list. Without telling it, a receipt that is already
        // saved and waiting would not appear until the page was reloaded — and
        // the person would be left looking at an upload that seemed to go
        // nowhere. (ZEN-513)
        qc.invalidateQueries({ queryKey: ["receipts"] });
      } catch (e) {
        // What went wrong, in the server's own words — and an honest status.
        // This used to mark EVERY failure "duplicate" and say "Upload failed"
        // with the reason thrown away (ZEN-125).
        const { status, errorMsg } = uploadFailureState(e);
        toast.error(status === "duplicate" ? errorMsg : `${file.name}: ${errorMsg}`);
        setQueue((q) => q.map((item) => item.id === qId ? { ...item, status, errorMsg } : item));
      } finally {
        setProcessingCount((c) => c - 1);
        if (fileRef.current) fileRef.current.value = "";
      }
    }
  };

  const removeFromQueue = (qId) => setQueue((q) => q.filter((item) => item.id !== qId));

  // THE SECOND ROUTE IS GONE. (ZEN-513, owner ruling 2026-09-02, scope settled
  // 2026-09-13.) There used to be a "Review all" button here that turned queued
  // receipts into rows of the cramped grid — so the SAME receipt could be
  // reviewed two ways, reached by two buttons on one page, and which one you got
  // decided whether you could read the date, the category and the currency. The
  // owner met both within an hour and the good one caught a real money error
  // (ZEN-514, a Canadian expense recorded as US dollars) that the cramped one hid.
  //
  // Uploaded receipts are stored the moment they arrive and are already waiting
  // in the labelled panel below, one at a time, with every field named. Nothing
  // routes them into the grid any more.
  //
  // The grid stays for SPREADSHEET rows, which is a different job: a spreadsheet
  // row has no receipt to look at, and the labelled panel is built around the
  // picture sitting beside the fields.


  const buildFromMapping = () => {
    const m = mapping;
    if (!m.original_amount) { toast.error("Map the Amount column to continue"); return; }
    const ds = (parsed.rows || []).map((row) => ({
      _id: uid(), student_id: student,
      transaction_date: m.transaction_date ? row[m.transaction_date] || "" : "",
      payee: m.payee ? row[m.payee] || "" : "",
      description: m.description ? row[m.description] || "" : "",
      category: "",
      _suggested_category: m.category && cats.includes(row[m.category]) ? row[m.category] : "",
      original_amount: m.original_amount ? toNum(row[m.original_amount]) : "",
      // Never guessed, never cut to three letters: a cell it cannot read stops
      // only this row until a currency is chosen. (ZEN-829)
      ...(() => {
        const read = readOutsideCurrency(m.original_currency ? row[m.original_currency] : "");
        return { original_currency: read.code, _unreadable_currency: read.unreadable };
      })(),
      eligible_529: m.eligible_529 ? (String(row[m.eligible_529]).trim() === "" ? null : truthy(row[m.eligible_529])) : null,
    })).filter((d) => d.original_amount !== "" || d.payee);
    setDrafts(ds); setStep(3);
  };

  const upd = (id, patch) => setDrafts((ds) => ds.map((d) => (d._id === id ? { ...d, ...patch } : d)));
  const remove = (id) => setDrafts((ds) => ds.filter((d) => d._id !== id));

  // A row whose spreadsheet currency could not be read waits on screen; the
  // other rows are not held up by it. (ZEN-829, ruling A)
  const ready = drafts.filter((d) => curCode(d.original_currency));
  const waiting = drafts.filter((d) => !curCode(d.original_currency));

  const expand = () => {
    const out = [];
    const payorFields = payorType === "party"
      ? { payor_type: "party", payor_party_id: payorPartyId, paid_by: "owner" }
      : { payor_type: payorType, payor_party_id: null, paid_by: ["student", "owner", "529"].includes(payorType) ? payorType : "owner" };
    for (const d of ready) {
      const base = { student_id: d.student_id, transaction_date: d.transaction_date, payee: d.payee, description: d.description,
        original_currency: d.original_currency, eligible_529: d.eligible_529 === true, draw_from_529: d.eligible_529 === true,
        eligibility_pending: d.eligible_529 !== true && d.eligible_529 !== false,
        // A ROW NOBODY HAS CLASSIFIED SENDS NO CATEGORY. It used to send one
        // regardless, so the receipt reading's guess was stored on a draft the
        // owner had not decided — and because the key was present, the server's
        // "that \"Other\" came from the import, not from you" guard could not
        // see it was a guess. The other two doors (the classify dialog and the
        // receipt review screen) already dropped it; this one did not. (ZEN-282)
        ...(d.eligible_529 === true || d.eligible_529 === false ? { category: d.category } : {}),
        reimbursed_by_ref: "none", ...payorFields,
        receipt_ids: d._receipt_id ? [d._receipt_id] : [] };
      // ONE ROW, ONE EXPENSE, and no tax year in the payload: the server reads
      // it off the payment date. The old branch here fanned one receipt out into
      // several expenses under hand-typed years — see ImportReviewRow. (ZEN-172)
      out.push({ ...base, original_amount: toNum(d.original_amount) });
    }
    return out;
  };

  const validate = (items) => {
    if (!payorType) return "Choose who paid (Paid by) for these expenses.";
    if (payorType === "party" && !payorPartyId) return "Select which party paid.";
    for (const d of ready) {
      if (!d.student_id) return "Every row needs a beneficiary.";
      if (!d.transaction_date) return `Row "${d.payee || "—"}" is missing a date.`;
      if (!d.payee) return "Every row needs a payee.";
      if (d.original_amount === "" || isNaN(toNum(d.original_amount))) return `Row "${d.payee}" needs an amount.`;
      if ((d.eligible_529 === true || d.eligible_529 === false) && !String(d.category || "").trim())
        return `Row "${d.payee}" is marked ${d.eligible_529 ? "529-eligible" : "not 529-eligible"} — choose a category for it, or clear that answer to decide later.`;
    }
    return null;
  };

  // ZEN-162: a row whose date the server flags is confirmed BY CONTENT, not
  // by position — editing the row changes its fingerprint, which drops the
  // confirmation and makes the server ask again. An index-keyed set here
  // would keep a confirmation pointed at whatever row slid into that slot.
  const [confirmedFps, setConfirmedFps] = useState(() => new Set());
  const [dateAsk, setDateAsk] = useState(null);
  const rowFp = (it) => `${it.student_id}|${it.transaction_date}|${it.payee}|${it.original_amount}`;

  const executeCommit = async () => {
    const items = expand().map((it) =>
      confirmedFps.has(rowFp(it)) ? { ...it, date_confirmed: true } : it);
    const sentIds = new Set(ready.map((d) => d._id));
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { items });
      toast.success(`Imported ${data.created} expense${data.created === 1 ? "" : "s"}`);
      // ── AND SAY SO WHEN A ROW TRIED TO SET THE TAX CREDIT. (ZEN-475)
      //
      // The server drops any AOTC reservation on an imported row and reports how
      // many it threw away. Without this the drop is SILENT, which was the open
      // half of ZEN-475 after the back end shipped: the app quietly declining to
      // do something a person's file asked for, on a screen about their taxes.
      //
      // A WARNING, NOT A SUCCESS NOTE, and it outlives the success one above:
      // this is not part of the good news. 12s matches the other notice in this
      // app that reports a saved-but-check-this outcome.
      //
      // Nothing a family uploads can trigger this today — the spreadsheet reader
      // knows seven columns and none is a credit column, and this screen builds
      // each row without these fields. It fires only for a request the browser
      // would not send, which is exactly the case where a silent drop is worst.
      const aotcNotice = aotcIgnoredNotice(data);
      if (aotcNotice) toast.warning(aotcNotice, { duration: 12000 });
      // This is the ONLY place in the app that puts an expense in the waiting
      // room. Without these the owner lands on a page whose "Needs a decision"
      // tab and navigation badge still read zero — the import worked, and the
      // app says there is nothing to do — until the cache goes stale on its own
      // half a minute later. (ZEN-113)
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["drafts-needing"] });
      qc.invalidateQueries({ queryKey: ["attention"] });
      // Remove the sent rows from the rows as they are NOW (React's updater). The
      // rows are locked while saving, but the list can still change meanwhile
      // (more files uploaded, Start over), and those changes are kept.
      // flushSync applies it before the next line, so what happens next is
      // decided here, from the rows really left — not in a later effect: a
      // message raised from an effect can land while the app's message area is
      // re-subscribing (sonner's Toaster re-subscribes whenever its own list
      // changes) and is then silently lost. (ZEN-829)
      let left = [];
      flushSync(() => setDrafts((current) => (left = rowsLeftAfterImport(current, sentIds))));
      if (!left.length) { nav("/expenses"); return; }
      const stillWaiting = left.filter((d) => !curCode(d.original_currency)).length;
      if (stillWaiting) toast.info(stillWaitingForCurrencyMessage(stillWaiting));
    } catch (e) {
      const refusal = dateConfirmRefusal(e);
      if (refusal) setDateAsk(refusal);
      else toast.error(apiErr(e));
    }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!ready.length) { toast.error("Nothing to import"); return; }
    const items = expand();
    const err = validate(items);
    if (err) { toast.error(err); return; }
    // NO "IMPORT ANYWAY" DOOR. (ZEN-513, owner ruling 2026-09-02: "I do not like
    // that" — remove it, do not soften it with a stronger warning.)
    await executeCommit();
  };

  return (
    <div>
      <PageHeader title="Imports & Receipts" subtitle="Upload receipts, spreadsheets, or photos — review everything before it's saved."
        actions={step > 1 ? <Btn variant="ghost" onClick={reset} data-testid="import-restart"><ArrowLeft size={16} weight="bold" /> Start over</Btn> : null} />

      <Stepper step={step} source={source} queueCount={queue.length} />

      {step === 1 && (
        <Card className="mt-4">
          <div className="mb-3 flex items-center gap-2">
            <Plus size={18} weight="bold" className="text-[#034AA2]" />
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#034AA2]">Create Expense(s) by Uploading Receipts, Spreadsheets, or Photos</h3>
          </div>
          {isStudentLocked ? (
            <div className="mb-3">
              <span className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">Importing for </span>
              <span className="text-sm font-semibold text-[#1f2937]" data-testid="import-student-locked">{selectedStudentObj?.full_name || "—"}</span>
            </div>
          ) : (
            <div className="mb-3">
              <label className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">Choose a beneficiary for this import</label>
              <select value={student} onChange={(e) => setStudent(e.target.value)} data-testid="import-student" className="h-10 w-full max-w-sm rounded-md border border-[#C2C9D1] bg-white px-2 text-sm">
                <option value="">Choose a beneficiary…</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            </div>
          )}
          {!isStudentLocked && !studentChosen ? (
            <div className="rounded-xl border-2 border-dashed border-[#D97706] bg-[#FCF5EE] p-6 text-center" data-testid="import-choose-student-first">
              <Warning size={34} weight="bold" className="mx-auto text-[#D97706]" />
              <div className="mt-2 text-sm font-bold text-[#92600A]">Choose a beneficiary above before uploading</div>
              <p className="mt-1 text-xs text-[#6b7280]">Receipts must be assigned to a specific beneficiary. Select one from the dropdown, then drag &amp; drop or choose files.</p>
            </div>
          ) : (
          <>
          <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={(e) => { e.preventDefault(); setDragging(false); }} onDrop={onDrop}
            onClick={() => fileRef.current?.click()} role="button"
            className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragging ? "border-[#034AA2] bg-[#034AA2]/10" : "border-[#C2C9D1] bg-[#F8FAFC] hover:bg-[#E9E9EB]/50"}`} data-testid="import-dropzone">
            <UploadSimple size={34} weight="bold" className="mx-auto text-[#034AA2]" />
            <div className="mt-2 text-base font-bold text-[#1f2937]">{dragging ? "Drop file(s) to upload" : "Drag & drop, or choose a file"}</div>
            <p className="mx-auto mt-1 max-w-md text-xs text-[#6b7280]">
              Drop in a <b>spreadsheet</b> (CSV, XLSX, XLS) or <b>receipt photos / PDFs</b> (iPhone and Android photos included). Drop several files at once and they'll queue up for batch review — the AI reads each one automatically.
            </p>
            <div className="mx-auto mt-2 flex max-w-md items-start gap-2 rounded-lg border border-[#D97706]/40 bg-[#FCF5EE] px-3 py-2 text-left" data-testid="import-one-receipt-note">
              <Receipt size={16} weight="bold" className="mt-0.5 shrink-0 text-[#92600A]" />
              <p className="text-[11px] leading-snug text-[#92600A]">
                <b>One receipt per photo.</b> Got several paper receipts? Photograph each one separately — a photo with more than one receipt on it won&rsquo;t be read correctly.
              </p>
            </div>
            <div className="mt-2 inline-flex flex-wrap justify-center gap-1.5" data-testid="import-accepted-formats">
              {["CSV", "XLSX", "XLS", "HEIC", "JPEG", "PNG", "WEBP", "AVIF", "PDF"].map((t) => (
                <span key={t} className="rounded-full bg-[#E9E9EB] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">{t}</span>
              ))}
            </div>
            <input ref={fileRef} type="file" accept={`.csv,.xlsx,.xls,${RECEIPT_ACCEPT}`} multiple className="hidden" onChange={(e) => { const files = e.target.files; if (files) for (let i = 0; i < files.length; i++) onFile(files[i]); }} data-testid="import-file-input" />
          </div>
          {/* On a phone, a plainly-labelled camera button — the same pair the beneficiary
              has on My Receipts. Its own input carries `capture` but NEVER `multiple`:
              on iOS Safari the two together force single-shot camera and HIDE the file
              chooser, which breaks the long-receipt scan-to-PDF path. Two separate
              controls keep both working. Gated the same way uploads are — it only
              renders once a beneficiary is chosen, and onFile re-checks. */}
          <div className="mt-3 flex justify-center">
            <Btn variant="green" onClick={() => camRef.current?.click()} data-testid="import-camera-btn"><Camera size={16} weight="bold" /> Take photo</Btn>
          </div>
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); if (camRef.current) camRef.current.value = ""; }} data-testid="import-camera-input" />
          </>
          )}
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-[#6b7280]">Tip: Download the blank template, fill it in (or share it with your beneficiary), then upload it here.</p>
            <Btn variant="ghost" onClick={downloadTemplate} data-testid="import-download-template"><FileCsv size={16} weight="bold" /> Download blank template</Btn>
          </div>

          {/* ---- Batch Receipt Queue ---- */}
          {queue.length > 0 && (
            <div className="mt-4 border-t border-[#C2C9D1] pt-4" data-testid="receipt-queue-panel">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#6b7280]">
                  <Receipt size={16} weight="bold" className="mr-1.5 inline -mt-0.5 text-[#034AA2]" />
                  Queued receipts ({queue.length})
                </h3>
                {processingCount > 0 ? (
                  <span className="flex items-center gap-2 rounded-md bg-[#FEF3C7] px-3 py-1.5 text-xs font-semibold text-[#92400E]" data-testid="queue-processing-badge">
                    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#D97706]/30 border-t-[#D97706]" />
                    AI is reading {processingCount === 1 ? "your receipt" : `${processingCount} receipts`} — review will be available once complete
                  </span>
                ) : null}
                {/* NO "REVIEW ALL" BUTTON HERE ANY MORE. It used to turn these
                    into rows of the cramped grid — the second way of reviewing
                    the same receipt, and the worse one. They are already waiting
                    in the labelled panel further down this page.

                    NOTHING IS SAID IN ITS PLACE ON PURPOSE: a sentence pointing
                    at the panel below would be new copy, and copy is the
                    owner's. Asked; not yet answered. (ZEN-513, rule 13.) */}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {queue.map((q) => { const pres = queueItemPresentation(q); return (
                  <div key={q.id} className={`flex items-start gap-2.5 rounded-lg border p-2.5 ${pres.tone}`} data-testid={`queue-item-${q.id}`} data-status={pres.kind}>
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-[#C2C9D1] bg-white overflow-hidden">
                      {q.receiptId ? (
                        <img src={receiptUrl(q.receiptId)} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                      ) : q.contentType?.includes("pdf") ? (
                        <FilePdf size={22} className="text-[#F01919]" />
                      ) : (
                        <ImageIcon size={22} className="text-[#034AA2]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold text-[#1f2937]">{q.filename}</div>
                      {pres.kind === "processing" && <div className="mt-0.5 text-[11px] text-[#D97706]">{pres.message}</div>}
                      {pres.kind === "error" && <div className="mt-0.5 text-[11px] text-[#D97706]">{pres.message}</div>}
                      {pres.kind === "duplicate" && <div className="mt-0.5 text-[11px] text-[#D97706] font-semibold">{pres.message}</div>}
                      {pres.kind === "failed" && <div className="mt-0.5 text-[11px] font-semibold text-[#F01919]" data-testid={`queue-failed-${q.id}`}>{pres.message}</div>}
                      {q.status === "ready" && q.draft && (
                        <div className="mt-0.5 text-[11px] text-[#6b7280]">
                          <b>{q.draft.payee || "Unknown"}</b> · {readAmount(q.draft) != null ? `${curCode(q.draft.original_currency, "USD")} ${readAmount(q.draft)}` : "?"} · {q.draft.category || "?"}
                        </div>
                      )}
                      {q.status === "ready" && readingFoundNothing({ draft: q.draft }) && (
                        <div className="mt-0.5 text-[11px] text-[#92600A]" data-testid={`reading-found-nothing-${q.id}`}>{READING_FOUND_NOTHING}</div>
                      )}
                    </div>
                    <button onClick={() => removeFromQueue(q.id)} className="shrink-0 rounded p-1 text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#F01919]" aria-label="remove" data-testid={`queue-remove-${q.id}`}><X size={14} weight="bold" /></button>
                  </div>
                ); })}
              </div>
            </div>
          )}
        </Card>
      )}

      {step === 1 && <BulkReceiptMatcher studentId={student} students={students} />}

      {step === 2 && parsed && (
        <Card className="mt-4" data-testid="import-mapping">
          <h3 className="text-sm font-bold uppercase text-[#6b7280]">Map your columns</h3>
          <p className="mb-3 text-xs text-[#6b7280]">We guessed the mapping — fix any that are wrong. "Amount" is required.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[["transaction_date", "Date"], ["payee", "Payee *"], ["original_amount", "Amount *"], ["original_currency", "Currency"], ["category", "Category"], ["description", "Description"], ["eligible_529", "529-eligible (Yes/No)"]].map(([field, label]) => (
              <label key={field} className="block">
                <span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">{label}</span>
                <select value={mapping[field] || ""} onChange={(e) => setMapping((m) => ({ ...m, [field]: e.target.value || undefined }))} data-testid={`map-${field}`} className="h-10 w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm">
                  <option value="">— Not mapped —</option>
                  {parsed.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-[#6b7280]">{parsed.rows.length} rows detected</span>
            <Btn onClick={buildFromMapping} data-testid="import-build-drafts">Preview {parsed.rows.length} rows <ArrowRight size={16} weight="bold" /></Btn>
          </div>
        </Card>
      )}

      {step === 3 && (
        <div className="mt-4">
          {drafts.length === 0 ? (
            <EmptyState icon={Warning} title="No rows to import" subtitle="Go back and try a different file or image." />
          ) : (
            <>
              <Card className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm"><b>{drafts.length}</b> item{drafts.length === 1 ? "" : "s"} ready · expands to <b>{expand().length}</b> expense{expand().length === 1 ? "" : "s"} <span className="inline-flex items-center text-[11px] text-[#6b7280]">· Currency <InfoTip testid="import-currency-info" text={CURRENCY_FIELD_HELP} /></span></div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2" data-testid="import-paidby">
                    <span className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">Paid by <span className="text-[#F01919]">*</span></span>
                    <select
                      value={payorType === "party" ? `party:${payorPartyId}` : payorType}
                      onChange={(e) => { const v = e.target.value; if (v.startsWith("party:")) { setPayorType("party"); setPayorPartyId(v.slice(6)); } else { setPayorType(v); setPayorPartyId(""); } }}
                      data-testid="import-paidby-select"
                      className={`h-9 rounded-md border bg-white px-2 text-sm font-semibold ${payorType ? "border-[#C2C9D1]" : "border-[#F01919] text-[#F01919]"}`}>
                      <option value="">— Select who paid —</option>
                      <option value="student">Student (each row's student)</option>
                      <option value="owner">{`Owner · ${ownerNick}`}</option>
                      <option value="529">529 account (paid directly to the school)</option>
                      {(parties?.custom || []).map((p) => <option key={p.id} value={`party:${p.id}`}>{p.nickname}</option>)}
                    </select>
                  </div>
                  {/* THE "n/n REVIEWED" TICK IS GONE WITH THE DIALOG IT FED.
                      Measured on origin/main: it was never stored — nothing was
                      sent to the server and nothing was written to the expense.
                      It changed one dialog and nothing else, so a person who
                      ticked it had no evidence they had, and nobody could ever
                      tell which stored expenses came in unchecked. With the
                      dialog removed it did literally nothing, and a control that
                      does nothing is worse than no control. (ZEN-513.)

                      RECORDING THAT A RECEIPT WAS REVIEWED — the other half of
                      that ruling — is a change to the server, which this lane
                      was told to stay out of. Split out rather than half-done. */}
                  <div className="flex flex-col items-end gap-1">
                    <DateConfirmBanner message={dateAsk ? dateFlagMessage(dateAsk, "owner") : null}
                      confirmed={false}
                      onConfirmedChange={(checked) => {
                        if (!checked || !dateAsk) return;
                        // MATCHED ON WHAT THE ROW IS, not on where it sat in
                        // the list. The position is the server's own count and
                        // is exact for the request that was refused — but the
                        // person may delete or reorder a row before ticking,
                        // and then the position points at somebody else's
                        // expense while the tick still says "yes, correct".
                        // Position is the fallback, never the first answer.
                        const rows = expand();
                        const it = rows.find((r) =>
                          r.payee === dateAsk.payee
                          && r.transaction_date === dateAsk.transaction_date)
                          || rows[(dateAsk.row || 0) - 1];
                        if (it) setConfirmedFps((prev) => new Set(prev).add(rowFp(it)));
                        setDateAsk(null);
                      }}
                      testid="import-date-confirm" />
                    <Btn variant="green" onClick={commit} disabled={busy || !ready.length} data-testid="import-commit"><CheckCircle size={16} weight="bold" /> {busy ? "Importing…" : importButtonLabel(expand().length, waiting.length)}</Btn>
                  </div>
                </div>
              </Card>

              {/* Side-by-side layout: receipt preview + rows */}
              {source === "image" && drafts.some((d) => d._receipt_id) ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                  {/* Receipt image panel */}
                  <div className="lg:col-span-4 xl:col-span-3">
                    <div className="sticky top-4">
                      <Card className="!p-2" data-testid="receipt-preview-panel">
                        <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">Receipt preview</div>
                        {previewReceiptId ? (
                          <ReceiptViewer key={previewReceiptId} getPageSrc={(page) => receiptPreviewUrl(previewReceiptId, page)} pageCount={drafts.find((d) => d._receipt_id === previewReceiptId)?._page_count || 1} alt="Receipt preview" />
                        ) : (
                          <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-[#C2C9D1] text-xs text-[#6b7280]">Click a receipt row to preview</div>
                        )}
                        {/* Receipt thumbnails strip */}
                        {(() => {
                          const receiptIds = [...new Set(drafts.map((d) => d._receipt_id).filter(Boolean))];
                          if (receiptIds.length <= 1) return null;
                          return (
                            <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1" data-testid="receipt-thumbnail-strip">
                              {receiptIds.map((rid) => (
                                <button key={rid} onClick={() => setPreviewReceiptId(rid)}
                                  className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border-2 transition-colors ${rid === previewReceiptId ? "border-[#034AA2] ring-1 ring-[#034AA2]" : "border-[#C2C9D1] hover:border-[#034AA2]/50"}`}
                                  data-testid={`receipt-thumb-${rid}`}>
                                  <img src={receiptPreviewUrl(rid)} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </Card>
                    </div>
                  </div>
                  {/* Review rows. Locked while an import is being saved: the save
                      sends the rows as they were when Import was pressed, so an
                      edit made meanwhile would be silently lost. (ZEN-829) */}
                  <div className={`space-y-2 lg:col-span-8 xl:col-span-9 ${busy ? "opacity-60" : ""}`} inert={busy} aria-busy={busy} data-testid="import-rows">
                    {drafts.map((d) => (
                      <div key={d._id} onClick={() => d._receipt_id && setPreviewReceiptId(d._receipt_id)} className={`cursor-pointer rounded-lg transition-shadow ${d._receipt_id === previewReceiptId ? "ring-2 ring-[#034AA2]/30" : ""}`}>
                        <ReviewRow d={d} students={students} currencies={currencies} onChange={(p) => upd(d._id, p)} onRemove={() => remove(d._id)} isStudentLocked={isStudentLocked} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`space-y-2 ${busy ? "opacity-60" : ""}`} inert={busy} aria-busy={busy} data-testid="import-rows">
                  {drafts.map((d) => <ReviewRow key={d._id} d={d} students={students} currencies={currencies} onChange={(p) => upd(d._id, p)} onRemove={() => remove(d._id)} isStudentLocked={isStudentLocked} />)}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ---- Receipt Queue (OCR uploads pending review) ---- */}
      <ReceiptQueue students={students} selectedStudentId={selectedStudentId} />

    </div>
  );
}

